import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email,
        name: name.trim() || null
      });
      
      if (response.data.success) {
        setIsSubscribed(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubscribed) {
    return (
      <section className="py-16 lg:py-24 bg-gradient-to-b from-charcoal to-surface-dark/50" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-8 border border-gold/30 bg-gold/5"
          >
            <CheckCircle size={48} className="text-gold mx-auto mb-4" />
            <h3 className="heading-section text-2xl text-stone mb-2">You're Subscribed!</h3>
            <p className="text-stone/60">
              Check your inbox for a welcome email with exclusive resources.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-charcoal to-surface-dark/50" data-testid="newsletter-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <Mail size={20} className="text-gold" />
            <span className="tech-label">//STAY_UPDATED</span>
          </div>
          <h2 className="heading-display text-3xl md:text-4xl text-stone mb-4">
            Industry <span className="text-gold">Insights</span>
          </h2>
          <p className="text-stone/50 max-w-xl mx-auto">
            Get technical guides, case studies, and product updates delivered to your inbox. 
            Join 2,500+ engineers staying ahead of the curve.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="max-w-xl mx-auto"
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input w-full"
                data-testid="newsletter-name"
              />
            </div>
            <div className="flex-1">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input w-full"
                required
                data-testid="newsletter-email"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary whitespace-nowrap"
              data-testid="newsletter-submit"
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              <ArrowRight size={16} className="ml-2" />
            </button>
          </div>
          <p className="text-stone/30 text-xs mt-4 text-center">
            No spam. Unsubscribe anytime. We respect your privacy.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
